// libraries.
import Table from 'react-bootstrap/Table';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { robotoRegular } from '@habitech/shared';

// selectors.
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import { getPricingTasks } from '@domains/documents-review/store/slices/document-review-slice';
import { useDocumentsTaskFilters } from './hooks/useDocumentsTaskFilters';
import { useFetchingDocumentsTasks } from './hooks/useFetchingDocumentsTasks';

// components.
import { TaskAction } from './TaskAction';
import { statusLabel } from './utils';
import { PendingTasks } from './PendingTasks';
import { DurationCell } from './components/TaskDuration/DurationCell';
import { BusinessType } from './components/BusinessType/BusinessType';
import { SortDataFilter } from '@shared/SortDataFilter/SortDataFilter';

// styles.
import { WrapperTable } from './styles';
import { DropdownFilter } from '@shared/DropdownFilter/DropdownFilter';

export const DocumentsTable = () => {
  const email = useSelector(getEmail);
  const taskList = useSelector(getPricingTasks) || [];
  const country = useSelector(getCountry) as 'MX' | 'CO';
  const [agentFilter, setAgentFilter] = useState(false);

  const {
    filteredData,
    sortOrder,
    toggleSortOrder,
    agentOptions,
    actionOptions,
    businessTypeOptions,
    handleFilterChange,
    filters,
  } = useDocumentsTaskFilters(taskList, country, email);

  const { refetch, isFetching } = useFetchingDocumentsTasks({
    status: filters.action.join(','),
    business_type: filters.business_type.join(','),
  });

  useEffect(() => {
    if (!agentFilter && agentOptions.length > 0) {
      const allAgentValues = agentOptions.map(opt => opt.value);
      const isEmailInList = email && allAgentValues.includes(email);

      // If the user's email is one of the agents, pre-filter to their tasks.
      // Otherwise leave the filter empty so ALL tasks are visible — picking
      // "todos" should never hide rows assigned outside the hardcoded list.
      handleFilterChange('agent', isEmailInList ? [email] : []);
      setAgentFilter(true);
    }
  }, [email, agentOptions, agentFilter, handleFilterChange]);

  if (isFetching) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100 fw-bold"
        style={{ fontFamily: robotoRegular }}
      >
        Cargando tareas...
      </div>
    );
  }

  return (
    <WrapperTable>
      <PendingTasks tasks={taskList} />
      <div className="table-scroll">
        <Table bordered hover>
          <thead>
            <tr className="text-center headers">
              <th>#</th>
              <th>
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <span>Fecha</span>
                  <SortDataFilter
                    order={sortOrder}
                    onToggle={toggleSortOrder}
                  />
                </div>
              </th>
              <th>SLA</th>
              <th>NID</th>
              <th>
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <span>Línea de negocio</span>
                  <DropdownFilter
                    options={businessTypeOptions}
                    value={filters.business_type}
                    onChange={selected =>
                      handleFilterChange('business_type', selected)
                    }
                  />
                </div>
              </th>
              <th>Tipo de tarea</th>
              <th>
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <span>Agente</span>
                  <DropdownFilter
                    options={agentOptions}
                    value={filters.agent}
                    onChange={selected => handleFilterChange('agent', selected)}
                  />
                </div>
              </th>
              <th>
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <span>Estado</span>
                  <DropdownFilter
                    options={actionOptions}
                    value={filters.action}
                    onChange={selected =>
                      handleFilterChange('action', selected)
                    }
                  />
                </div>
              </th>
              <th></th>
            </tr>
          </thead>

          <tbody className="align-middle text-center rows">
            {filteredData.length > 0 ? (
              filteredData.map((row, index) => (
                <tr key={row.nid}>
                  <td>{index + 1}</td>
                  <td>{row.date}</td>
                  <td>
                    <DurationCell
                      startAt={row.date}
                      updatedAt={row.updated_at}
                      country={country}
                      action={row.action}
                    />
                  </td>
                  <td>{row.nid}</td>
                  <td>
                    <BusinessType businessType={row.business_type} />
                  </td>
                  <td>{row.task_type}</td>
                  <td>{row.agent || 'Sin asignar'}</td>
                  <td>{statusLabel[row.action]}</td>
                  <td>
                    <TaskAction
                      action={row.action}
                      nid={row.nid}
                      task={row}
                      onErrorRefetch={refetch}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center fw-bold">
                  No hay tareas que coincidan con este filtro.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </WrapperTable>
  );
};
